import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Formik } from "formik";
import * as Yup from "yup";
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	IconButton,
	InputAdornment,
	Radio,
	RadioGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Loader } from "../../shared";
import ChangesFormActions from "../../shared/formChanges/ChangesFormActions";
import { CustomTextField } from "../../shared/CustomTextField";
import { mcpServerService } from "../../../services/mcpServers";
import { McpServerAuthenticationType, McpServerDto, McpServerTestConnectionResultDto } from "../../../types/mcpServer";
import { MenuRoutes } from "../../../types/routes";
import { CCMAXLENGTHS, HTTPSTATUSCODES } from "../../../utils/constants";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const CREDENTIAL_MASKED = "••••••••";

interface FormValues {
	name: string;
	serverUrl: string;
	authenticationType: McpServerAuthenticationType;
	credential: string;
	credentialHeaderName: string;
}

const AddEditMcpServer: FC = () => {
	const { mcpServerId } = useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isMountedRef = useIsMountedRef();
	const { enqueueSnackbar } = useSnackbar();
	const revealedCredentialRef = useRef<string | null>(null);

	const [server, setServer] = useState<McpServerDto | null>(null);
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(!mcpServerId);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showCredential, setShowCredential] = useState<boolean>(false);
	const [isLoadingCredential, setIsLoadingCredential] = useState<boolean>(false);
	const [isTestingConnection, setIsTestingConnection] = useState<boolean>(false);
	const [connectionResult, setConnectionResult] = useState<McpServerTestConnectionResultDto | null>(null);

	useEffect(() => {
		if (mcpServerId) {
			loadServer();
		}
	}, [mcpServerId]);

	const loadServer = async () => {
		try {
			const response = await mcpServerService.getMcpServerById(mcpServerId);
			if (isMountedRef.current && response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setServer(response.data);
				setIsDataLoaded(true);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleCancel = (dirty: boolean, resetForm: () => void) => {
		if (mcpServerId && dirty) {
			resetForm();
			return;
		}
		navigate(MenuRoutes.McpServers);
	};

	const handleSubmit = async (values: FormValues) => {
		setIsSubmitting(true);
		try {
			const payload: McpServerDto = {
				id: mcpServerId ?? "00000000-0000-0000-0000-000000000000",
				name: values.name,
				serverUrl: values.serverUrl,
				authenticationType: values.authenticationType,
				credential: values.authenticationType === McpServerAuthenticationType.KeyBased
					? values.credential
					: null,
				credentialHeaderName: values.authenticationType === McpServerAuthenticationType.KeyBased
					? (values.credentialHeaderName.trim() || null)
					: null,
				agentNames: [],
			};

			const response = mcpServerId
				? await mcpServerService.updateMcpServer(mcpServerId, payload)
				: await mcpServerService.createMcpServer(payload);

			if (response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				enqueueSnackbar(
					t(mcpServerId ? "McpServer.UpdateSucceeded" : "McpServer.CreateSucceeded"),
					{ variant: "success" }
				);
				navigate(MenuRoutes.McpServers);
			}
		} catch (error) {
			if (error?.response?.status === HTTPSTATUSCODES.StatusCodeConflict) {
				enqueueSnackbar(t("McpServer.NameAlreadyExists"), { variant: "error" });
			} else if (error?.response?.status === HTTPSTATUSCODES.StatusCodeBadRequest) {
				enqueueSnackbar(
					error.response.data?.message ?? t(mcpServerId ? "McpServer.UpdateFailed" : "McpServer.CreateFailed"),
					{ variant: "error" }
				);
			} else {
				enqueueSnackbar(
					t(mcpServerId ? "McpServer.UpdateFailed" : "McpServer.CreateFailed"),
					{ variant: "error" }
				);
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleTestConnection = async (values: FormValues) => {
		setIsTestingConnection(true);
		setConnectionResult(null);
		try {
			const response = await mcpServerService.testConnection(
				mcpServerId ?? "00000000-0000-0000-0000-000000000000",
				{
					serverUrl: values.serverUrl,
					authenticationType: values.authenticationType,
					credential: values.authenticationType === McpServerAuthenticationType.KeyBased
						? values.credential
						: null,
					credentialHeaderName: values.authenticationType === McpServerAuthenticationType.KeyBased
						? (values.credentialHeaderName.trim() || null)
						: null,
				}
			);
			if (isMountedRef.current && response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				setConnectionResult(response.data);
			}
		} catch {
			if (isMountedRef.current) {
				setConnectionResult({ success: false, message: t("McpServer.ConnectionError") });
			}
		} finally {
			if (isMountedRef.current) {
				setIsTestingConnection(false);
			}
		}
	};

	const initialValues: FormValues = {
		name: server?.name ?? "",
		serverUrl: server?.serverUrl ?? "",
		authenticationType: server?.authenticationType ?? McpServerAuthenticationType.Unauthenticated,
		credential: server?.credential ?? "",
		credentialHeaderName: server?.credentialHeaderName ?? "",
	};

	return (
		<>
			<Loader isOpen={!isDataLoaded} isInline={false} />
			{isDataLoaded && (
				<Card sx={{ pl: 3, pb: 4, pr: 3, pt: 4 }}>
					<Formik
						initialValues={initialValues}
						validationSchema={Yup.object().shape({
							name: Yup.string()
								.required(t("McpServer.NameRequired"))
								.max(CCMAXLENGTHS.McpServerName, t("McpServer.NameTooLong")),
							serverUrl: Yup.string()
								.required(t("McpServer.ServerUrlRequired"))
								.url(t("McpServer.ServerUrlInvalid"))
								.max(CCMAXLENGTHS.McpServerUrl, t("McpServer.ServerUrlTooLong")),
							credential: Yup.string().when("authenticationType", {
								is: McpServerAuthenticationType.KeyBased,
								then: (schema) => mcpServerId
									? schema
									: schema.required(t("McpServer.CredentialRequired")),
								otherwise: (schema) => schema,
							}),
						})}
						onSubmit={handleSubmit}
					>
						{({ errors, handleBlur, handleChange, handleSubmit: formikSubmit, touched, values, isValid, dirty, resetForm, setFieldValue }) => (
							<form onSubmit={formikSubmit}>
								<ChangesFormActions
									isValid={true}
									isSubmitting={isSubmitting}
									handleReset={() => handleCancel(dirty, resetForm)}
									isSaveAction={false}
								/>
								<Grid container spacing={3}>
									{/* Name */}
									<Grid item xs={12} md={6}>
										<CustomTextField
											fullWidth
											size="small"
											name="name"
											placeholder={t("McpServer.EnterName")}
											label={`${t("McpServer.Name")} *`}
											value={values.name}
											onChange={handleChange}
											onBlur={handleBlur}
											error={Boolean(touched.name && errors.name)}
											helperText={touched.name && errors.name}
										/>
									</Grid>

									{/* Server URL */}
									<Grid item xs={12} md={6}>
										<CustomTextField
											fullWidth
											size="small"
											name="serverUrl"
											placeholder={t("McpServer.EnterServerUrl")}
											label={`${t("McpServer.ServerUrl")} *`}
											value={values.serverUrl}
											onChange={handleChange}
											onBlur={handleBlur}
											error={Boolean(touched.serverUrl && errors.serverUrl)}
											helperText={touched.serverUrl && errors.serverUrl}
										/>
									</Grid>

									{/* Authentication Type */}
									<Grid item xs={12}>
										<FormControl component="fieldset">
											<FormLabel component="legend">
												{t("McpServer.AuthType")}
											</FormLabel>
											<RadioGroup
												row
												name="authenticationType"
												value={values.authenticationType}
												onChange={(e) => {
													const val = parseInt(e.target.value) as McpServerAuthenticationType;
													setFieldValue("authenticationType", val);
													if (val !== McpServerAuthenticationType.KeyBased) {
														setFieldValue("credential", "");
														setFieldValue("credentialHeaderName", "");
													}
												}}
											>
												<FormControlLabel
													value={McpServerAuthenticationType.Unauthenticated}
													control={<Radio size="small" />}
													label={t("McpServer.AuthTypeUnauthenticated")}
												/>
												<FormControlLabel
													value={McpServerAuthenticationType.KeyBased}
													control={<Radio size="small" />}
													label={t("McpServer.AuthTypeKeyBased")}
												/>
											</RadioGroup>
										</FormControl>
									</Grid>

									{/* Credential header name + Credential (key-based only) */}
									{values.authenticationType === McpServerAuthenticationType.KeyBased && (
										<>
											<Grid item xs={12}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													<Typography variant="subtitle2">
														{t("McpServer.CredentialSectionTitle")} *
													</Typography>
													<Tooltip title={t("McpServer.CredentialSectionTooltip")}>
														<Info color="primary" fontSize="small" />
													</Tooltip>
												</Box>
											</Grid>
											<Grid item xs={12} md={6}>
												<CustomTextField
													fullWidth
													size="small"
													name="credentialHeaderName"
													placeholder={t("McpServer.CredentialHeaderNamePlaceholder")}
													label={t("McpServer.CredentialHeaderName")}
													value={values.credentialHeaderName}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
											</Grid>
											<Grid item xs={12} md={6}>
												<CustomTextField
													fullWidth
													size="small"
													name="credential"
													placeholder={mcpServerId ? t("McpServer.CredentialPlaceholderEdit") : t("McpServer.CredentialPlaceholder")}
													label={`${t("McpServer.Credential")}${!mcpServerId ? " *" : ""}`}
													value={values.credential}
													onChange={(e) => {
														if (e.target.value === CREDENTIAL_MASKED) {
															return;
														}
														handleChange(e);
													}}
													onFocus={() => {
														if (values.credential === CREDENTIAL_MASKED) {
															setFieldValue("credential", "");
														}
													}}
													onBlur={(e) => {
														if (showCredential) {
															setShowCredential(false);
															if (revealedCredentialRef.current !== null && values.credential === revealedCredentialRef.current) {
																setFieldValue("credential", CREDENTIAL_MASKED);
																revealedCredentialRef.current = null;
															}
														}
														handleBlur(e);
													}}
													error={Boolean(touched.credential && errors.credential)}
													helperText={touched.credential && errors.credential}
													type={showCredential ? "text" : "password"}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton
																	type="button"
																	size="small"
																	disabled={isLoadingCredential}
																	onMouseDown={(e) => e.preventDefault()}
																	onClick={async () => {
																		if (!showCredential && values.credential === CREDENTIAL_MASKED && mcpServerId) {
																			setIsLoadingCredential(true);
																			try {
																				const response = await mcpServerService.getCredential(mcpServerId);
																				if (isMountedRef.current && response?.status === HTTPSTATUSCODES.StatusCodeSuccess) {
																					revealedCredentialRef.current = response.data;
																					setFieldValue("credential", response.data);
																					setShowCredential(true);
																				}
																			} catch {
																				// credential stays masked
																			} finally {
																				if (isMountedRef.current) setIsLoadingCredential(false);
																			}
																		} else {
																			if (showCredential && revealedCredentialRef.current !== null && values.credential === revealedCredentialRef.current) {
																				setFieldValue("credential", CREDENTIAL_MASKED);
																				revealedCredentialRef.current = null;
																			}
																			setShowCredential((prev) => !prev);
																		}
																	}}
																>
																	{isLoadingCredential ? <CircularProgress size={16} /> : showCredential ? <VisibilityOffIcon /> : <VisibilityIcon />}
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</Grid>
										</>
									)}

									{/* Used by AI Agents (edit mode only) */}
									{mcpServerId && server?.agentNames?.length > 0 && (
										<Grid item xs={12}>
											<Typography variant="subtitle2" gutterBottom>
												{t("McpServer.UsedByFollowingAgents")}
											</Typography>
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
												{server.agentNames.map((name) => (
													<Chip
														key={name}
														label={name}
														sx={{ backgroundColor: "primary.light", color: "text.primary" }}
													/>
												))}
											</Box>
										</Grid>
									)}

									{/* Test Connection */}
									<Grid item xs={12}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
											<Button
												variant="outlined"
												disabled={!values.serverUrl || isTestingConnection}
												onClick={() => handleTestConnection(values)}
												startIcon={isTestingConnection ? <CircularProgress size={16} /> : undefined}
											>
												{t("McpServer.TestConnection")}
											</Button>
											{connectionResult && (
												<Alert
													severity={connectionResult.success ? "success" : "error"}
													sx={{ py: 0 }}
												>
													{connectionResult.success
														? t("McpServer.ConnectionSuccessful")
														: connectionResult.message}
												</Alert>
											)}
										</Box>
									</Grid>
								</Grid>
							</form>
						)}
					</Formik>
				</Card>
			)}
		</>
	);
};

export default AddEditMcpServer;
